import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Context
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";

// Components
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";

// Pages
import HomePage from "@/pages/HomePage";
import MenuPage from "@/pages/MenuPage";
import ProductPage from "@/pages/ProductPage";
import CustomTacoPage from "@/pages/CustomTacoPage";
import CartPage from "@/pages/CartPage";
import OrderTypePage from "@/pages/OrderTypePage";
import CheckoutPage from "@/pages/CheckoutPage";
import OrderPage from "@/pages/OrderPage";
import OrdersPage from "@/pages/OrdersPage";
import ProfilePage from "@/pages/ProfilePage";
import LoginPage from "@/pages/LoginPage";
import DemoAccess from "@/pages/DemoAccess";
import StaffLogin from "@/pages/StaffLogin";
import PublicOrderDisplay from "@/pages/PublicOrderDisplay";


// Admin Pages
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminKitchen from "@/pages/admin/AdminKitchen";
import AdminMenu from "@/pages/admin/AdminMenu";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminOffers from "@/pages/admin/AdminOffers";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminCashReport from "@/pages/admin/AdminCashReport";
import AdminPOS from "@/pages/admin/AdminPOS";
import AdminQRMesas from "@/pages/admin/AdminQRMesas";
import AdminCustomTaco from "@/pages/admin/AdminCustomTaco";
import AdminIntegrations from "@/pages/admin/AdminIntegrations";
import AdminVeriFactu from "@/pages/admin/AdminVeriFactu";
import AdminQRDisplay from "@/pages/admin/AdminQRDisplay";
import TableView from "@/pages/admin/TableView";
import TableLayoutEditor from "@/pages/admin/TableLayoutEditor";
import AdminBranches from "@/pages/admin/AdminBranches";
import MaintenancePage from "@/pages/MaintenancePage";

const IS_MAINTENANCE = true; // Set to false to reactive the system


// Driver Pages
import DriverDashboard from "@/pages/driver/DriverDashboard";
import DriverOrders from "@/pages/driver/DriverOrders";
import DriverLogin from "@/pages/DriverLogin";

// Auth Callback Component
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const { processSession, loginWithToken } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    const search = location.search;

    // Combine search and hash properly to parse all params
    const searchPart = search.startsWith('?') ? search.substring(1) : search;
    const hashPart = hash.startsWith('#') ? hash.substring(1) : hash;
    const combinedParams = [searchPart, hashPart].filter(Boolean).join('&');
    const params = new URLSearchParams(combinedParams);

    const sessionId = params.get("session_id");
    const token = params.get("token");
    const returnTo = params.get("returnTo") || "/menu";

    console.log(`[AuthCallback] Processing login. Token: ${token ? 'found' : 'missing'}, SessionId: ${sessionId ? 'found' : 'missing'}`);

    if (token) {
      // DEFINITIVE FIX: Token passed in hash
      loginWithToken(token).then((user) => {
        if (user) {
          console.log(`[AuthCallback] Login success for ${user.email}, navigating to ${returnTo}`);
          // Redirect based on user role if returnTo is just /menu (default)
          let finalDest = returnTo;
          if (finalDest === "/menu") {
            if (user.role === "admin" || user.role === "support") finalDest = "/admin";
            else if (user.role === "driver") finalDest = "/driver";
            else if (user.role === "cashier") finalDest = "/admin/pos";
            else if (user.role === "screen") finalDest = "/pedidos-local";
          }
          navigate(finalDest, { replace: true });
        } else {
          console.error("[AuthCallback] Token validation failed");
          navigate("/login", { replace: true });
        }
      });
    } else if (sessionId) {
      // Legacy sessionId support
      processSession(sessionId).then((user) => {
        if (user) {
          if (user.role === "admin" || user.role === "support") navigate("/admin", { replace: true });
          else if (user.role === "driver") navigate("/driver", { replace: true });
          else navigate("/menu", { replace: true });
        } else {
          navigate("/login", { replace: true });
        }
      });
    } else {
      console.warn("[AuthCallback] No login data found in URL");
      navigate("/login", { replace: true });
    }
  }, [location, navigate, processSession, loginWithToken]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">Iniciando sesión...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, roles = [] }) => {
  const { user, isLoading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(!location.state?.user);

  useEffect(() => {
    if (location.state?.user) {
      setIsChecking(false);
      return;
    }

    const verify = async () => {
      const authenticated = await checkAuth();
      if (!authenticated) {
        navigate("/login", { replace: true });
      }
      setIsChecking(false);
    };

    verify();
  }, [checkAuth, navigate, location.state]);

  if (isChecking || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const currentUser = location.state?.user || user;

  if (!currentUser) {
    return null;
  }

  // Si tiene el rol, renderizar. Si no, redirigir vía useEffect para evitar errores de renderizado
  if (roles.length > 0 && !roles.includes(currentUser.role)) {
    let dest = "/";
    if (currentUser.role === "cashier") dest = "/admin/pos";
    else if (currentUser.role === "screen") dest = "/pedidos-local";
    else if (currentUser.role === "driver") dest = "/driver";

    return <Navigate to={dest} replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check for login signals in URL hash/search BEFORE rendering routes
  const hasToken = location.hash?.includes("token=") || location.search?.includes("token=");
  const hasSession = location.hash?.includes("session_id=") || location.search?.includes("session_id=");
  const isAuthCallback = location.pathname === "/auth/callback" || hasToken || hasSession;

  console.log(`[AppRouter] Path: ${location.pathname}, AuthCallback: ${isAuthCallback}, HasToken: ${hasToken}`);

  if (IS_MAINTENANCE) {
    return <MaintenancePage />;
  }

  if (isAuthCallback) {

    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/menu" element={<MenuPage />} />
      <Route path="/menu/:itemId" element={<ProductPage />} />
      <Route path="/crea-tu-taco" element={<CustomTacoPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login/driver" element={<DriverLogin />} />
      <Route path="/demo" element={<DemoAccess />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/staff" element={<StaffLogin />} />

      {/* Cart is public, order-type and checkout require auth */}
      <Route path="/cart" element={<CartPage />} />
      <Route path="/order-type" element={<OrderTypePage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/order/:orderId" element={<OrderPage />} />
      <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/pedidos-local" element={<PublicOrderDisplay />} />

      {/* Admin Routes - allow cashier and kitchen for their specific pages */}
      <Route path="/admin" element={<ProtectedRoute roles={["admin", "support"]}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/kitchen" element={<ProtectedRoute roles={["admin", "kitchen", "support"]}><AdminKitchen /></ProtectedRoute>} />
      <Route path="/admin/menu" element={<ProtectedRoute roles={["admin", "support"]}><AdminMenu /></ProtectedRoute>} />
      <Route path="/admin/orders" element={<ProtectedRoute roles={["admin", "cashier", "support"]}><AdminOrders /></ProtectedRoute>} />
      <Route path="/admin/orders/:orderId" element={<ProtectedRoute roles={["admin", "cashier", "support"]}><AdminOrders /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute roles={["admin", "support"]}><AdminUsers /></ProtectedRoute>} />        <Route path="/admin/integrations" element={<ProtectedRoute roles={["admin", "support"]}><AdminIntegrations /></ProtectedRoute>} />      <Route path="/admin/verifactu" element={<ProtectedRoute roles={["admin", "support"]}><AdminVeriFactu /></ProtectedRoute>} />      <Route path="/admin/cash" element={<ProtectedRoute roles={["admin", "cashier", "support"]}><AdminCashReport /></ProtectedRoute>} />
      <Route path="/admin/pos" element={<ProtectedRoute roles={["admin", "cashier", "support"]}><AdminPOS /></ProtectedRoute>} />
      <Route path="/admin/qr-mesas" element={<ProtectedRoute roles={["admin", "support"]}><AdminQRMesas /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute roles={["admin", "support"]}><AdminSettings /></ProtectedRoute>} />
      <Route path="/admin/offers" element={<ProtectedRoute roles={["admin", "support"]}><AdminOffers /></ProtectedRoute>} />
      <Route path="/admin/custom-taco" element={<ProtectedRoute roles={["admin", "support"]}><AdminCustomTaco /></ProtectedRoute>} />
      <Route path="/admin/qr-display" element={<ProtectedRoute roles={["admin", "support"]}><AdminQRDisplay /></ProtectedRoute>} />
      <Route path="/admin/tables" element={<ProtectedRoute roles={["admin", "cashier", "support"]}><TableView /></ProtectedRoute>} />
      <Route path="/admin/tables/editor" element={<ProtectedRoute roles={["admin", "support"]}><TableLayoutEditor /></ProtectedRoute>} />
      <Route path="/admin/branches" element={<ProtectedRoute roles={["admin", "support"]}><AdminBranches /></ProtectedRoute>} />

      {/* Driver Routes */}
      <Route path="/driver" element={<ProtectedRoute roles={["driver", "admin", "support"]}><DriverDashboard /></ProtectedRoute>} />
      <Route path="/driver/orders" element={<ProtectedRoute roles={["driver", "admin", "support"]}><DriverOrders /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <AppRouter />
          <Toaster position="top-center" richColors />
          <PWAInstallPrompt />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
